/**
 * Familles d'état d'un camion sur la carte.
 *
 * La table n'est plus une liste de neuf statuts recopiée à la main (qui laissait
 * grise la moitié du parcours réel : frontière, chargement, retour…) : elle est
 * dérivée de `TRIP_STATUS_ORDER` (lib/tripPhases.js, miroir du parcours du
 * serveur) par des règles sur le NOM de l'étape. Une étape ajoutée au parcours
 * tombe dans la bonne famille sans toucher à ce fichier, et un test échoue si
 * une étape du serveur ne tombe dans aucune.
 *
 * La précision (l'étape exacte) reste dite en toutes lettres dans l'infobulle
 * et la fenêtre du camion : la couleur trie, le texte précise.
 */
import { TRIP_STATUS_ORDER } from '../../lib/tripPhases';
import { SIGNAL_LABELS, estArretLong } from './fleetRules';

export const FAMILLES = ['route', 'customs', 'handling', 'done', 'available', 'inactive', 'unknown'];

export const GROUP_LABELS = {
  route: 'En route',
  customs: 'À la frontière / douane',
  handling: 'Chargement / dépôt',
  done: 'Livré',
  available: 'Disponible',
  inactive: 'Hors service',
  unknown: 'Non précisé',
};

/**
 * Teintes : les trois premières sont les teintes catégorielles validées
 * (`--cat-*`). « Chargement / dépôt » et « Disponible » ont leurs propres
 * jetons ; ces deux choix sont isolés ici pour être changés d'un seul geste.
 */
export const GROUP_COLORS = {
  route: 'hsl(var(--cat-route))',
  customs: 'hsl(var(--cat-hold))',
  handling: 'hsl(var(--cat-load))',
  done: 'hsl(var(--cat-done))',
  available: 'hsl(var(--cat-available))',
  inactive: 'hsl(var(--muted-foreground))',
  unknown: 'hsl(var(--muted-foreground))',
};

/** Règles sur le nom de l'étape ; la première qui correspond gagne. */
const REGLES_ETAPE = [
  [/^(AT_CUSTOMS|CUSTOMS_)/, 'customs'],
  [/^(EN_ROUTE|CLEARED|IN_TRANSIT)/, 'route'],
  [/^(LOADING|LOADED|ARRIVED_AT_DEPOT)$/, 'handling'],
  // Après la livraison : déchargement, preuve de livraison, retour, étapes de facturation.
  [/^(DELIVERED|PARTIALLY_DELIVERED|UNLOADED|POD_|COMPLETED|RETURN|BILLING_|BLOCKED_AUDIT|FAILED_RECONCILIATION)/, 'done'],
  [/^(CREATED|VALIDATED|CANCELLED)$/, 'available'],
];

export const familleDeEtape = (etape) => {
  const code = String(etape || '').trim().toUpperCase();
  if (!code) return null;
  const regle = REGLES_ETAPE.find(([motif]) => motif.test(code));
  return regle ? regle[1] : 'unknown';
};

/** Étape → famille, pour tout le parcours réel (+ les anciens codes). */
export const STATUS_GROUP = Object.fromEntries(
  [...TRIP_STATUS_ORDER, 'CANCELLED', 'IN_TRANSIT', 'VALIDATED', 'LOADED', 'CUSTOMS_DEPARTURE', 'CUSTOMS_ARRIVAL', 'PARTIALLY_DELIVERED', 'RETURNING'].map(
    (etape) => [etape, familleDeEtape(etape)],
  ),
);

/** États du camion lui-même qui l'écartent de l'exploitation quand il n'a pas de trajet. */
const ETATS_HORS_SERVICE = new Set([
  'OUT_OF_SERVICE',
  'IMMOBILIZED',
  'MAINTENANCE',
  'RENTED_OUT',
  'RETURNED_TO_OWNER',
]);

/**
 * La famille d'une ligne de la flotte.
 *  - une étape de trajet connue décide ;
 *  - sans trajet : « Hors service » si la fiche du camion le dit, sinon
 *    « Disponible » (un camion équipé d'un boîtier, libre).
 */
export const familleDuCamion = (vehicule) => {
  const parEtape = familleDeEtape(vehicule?.trip_status);
  if (parEtape && parEtape !== 'available') return parEtape;
  const etatCamion = String(vehicule?.truck?.current_status || vehicule?.truck?.status || '').toUpperCase();
  if (ETATS_HORS_SERVICE.has(etatCamion) && !vehicule?.trip_id) return 'inactive';
  if (parEtape === 'available') return 'available';
  return vehicule?.trip_id ? 'unknown' : 'available';
};

/**
 * Un arrêt long ne surprend qu'en route : à la frontière, au dépôt, chez le
 * client ou sans mission, attendre est le travail normal.
 */
export const arretInquietant = (vehicule, maintenant = Date.now()) =>
  familleDuCamion(vehicule) === 'route' && estArretLong(vehicule, maintenant);

export const statusColor = (etape) => GROUP_COLORS[familleDeEtape(etape) || 'unknown'];
export const couleurFamille = (famille) => GROUP_COLORS[famille] || GROUP_COLORS.unknown;
export const libelleFamille = (famille) => GROUP_LABELS[famille] || GROUP_LABELS.unknown;

/**
 * La légende unique des cartes de flotte : les familles ET le liseré de signal,
 * tels que `truckIcon` les dessine. `ring` = pastille creuse à liseré coloré.
 */
export const FLEET_LEGEND = [
  ...['route', 'customs', 'handling', 'done', 'available', 'inactive'].map((cle) => ({
    key: cle,
    label: GROUP_LABELS[cle],
    color: GROUP_COLORS[cle],
  })),
  { key: 'signal-on', label: SIGNAL_LABELS.on, ring: 'hsl(var(--success))' },
  { key: 'signal-off', label: SIGNAL_LABELS.off, ring: 'hsl(var(--destructive))' },
];
